import { useEffect, useState } from "react";
import { Image } from "antd";
import { ExpandOutlined, LeftOutlined, RightOutlined } from "@ant-design/icons";
import PhotoCarousel from "./PhotoCarousel";

/**
 * The listing's photos: a sliding track, a counter, and a filmstrip to jump
 * straight to a shot. Arrow keys work once the gallery has focus, so a broker
 * can page through photos without reaching for the mouse.
 *
 * The track stops at both ends rather than wrapping. Wrapping would rewind the
 * whole strip past every photo in between, which reads as a glitch — and it
 * matches how Prev / Next behave for the listings themselves.
 *
 * A listing with no photos gets a deliberate placeholder — more than half the
 * listings in the system have none, so this is the common case, not the edge.
 */
const ListingPhotoGallery = ({ photos = [], badge }) => {
  const [index, setIndex] = useState(0);
  const [failed, setFailed] = useState({});
  const [viewerOpen, setViewerOpen] = useState(false);

  // Switching listings via Prev / Next reuses this component, so the shown
  // photo has to reset when the set changes.
  useEffect(() => {
    setIndex(0);
    setFailed({});
    setViewerOpen(false);
  }, [photos]);

  const count = photos.length;
  const atStart = index === 0;
  const atEnd = index >= count - 1;

  const step = (delta) =>
    setIndex((i) => Math.min(Math.max(i + delta, 0), count - 1));

  const onKeyDown = (event) => {
    if (count < 2) return;
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      step(-1);
    }
    if (event.key === "ArrowRight") {
      event.preventDefault();
      step(1);
    }
  };

  return (
    <div>
      <div
        role="group"
        aria-label="Listing photos"
        tabIndex={count > 1 ? 0 : -1}
        onKeyDown={onKeyDown}
        className="relative h-72 w-full overflow-hidden rounded-lg bg-light_gray focus:outline-none focus:ring-2 focus:ring-primary"
      >
        <PhotoCarousel
          photos={photos}
          index={index}
          failed={failed}
          onFailed={(src) => setFailed((f) => ({ ...f, [src]: true }))}
          emptyLabel="No photos for this listing"
        />

        {/* The only way into the viewer. The photo itself stays inert, so a
            click meant for the arrows or a drag across the image never throws a
            full-screen overlay in front of the person using the page. */}
        {count > 0 && (
          <button
            type="button"
            onClick={() => setViewerOpen(true)}
            className="absolute bottom-3 left-3 z-10 inline-flex items-center gap-1.5 rounded-sm bg-dark_gray/85 px-2 py-1 text-[11px] font-bold text-white transition-colors hover:bg-dark_gray focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            <ExpandOutlined /> View full size
          </button>
        )}

        {badge && (
          <span className="pointer-events-none absolute left-3 top-3 z-10 rounded-sm bg-blue px-2 py-1 text-[11px] font-extrabold tracking-wide text-white">
            {badge}
          </span>
        )}

        {count > 1 && (
          <>
            <button
              type="button"
              aria-label="Previous photo"
              disabled={atStart}
              onClick={() => step(-1)}
              className="absolute left-3 top-1/2 z-10 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-dark_gray/60 text-white transition-opacity hover:bg-dark_gray/80 disabled:pointer-events-none disabled:opacity-0"
            >
              <LeftOutlined />
            </button>
            <button
              type="button"
              aria-label="Next photo"
              disabled={atEnd}
              onClick={() => step(1)}
              className="absolute right-3 top-1/2 z-10 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-dark_gray/60 text-white transition-opacity hover:bg-dark_gray/80 disabled:pointer-events-none disabled:opacity-0"
            >
              <RightOutlined />
            </button>
            <span className="pointer-events-none absolute bottom-3 right-3 z-10 rounded-sm bg-dark_gray/85 px-2 py-1 text-[11px] font-bold text-white">
              {index + 1} / {count}
            </span>
          </>
        )}
      </div>

      {count > 0 && (
        <div className="hidden">
          <Image.PreviewGroup
            items={photos}
            preview={{
              visible: viewerOpen,
              current: index,
              onVisibleChange: (open) => setViewerOpen(open),
              // Closing the viewer leaves the carousel on whatever photo was
              // last looked at, rather than snapping back.
              onChange: (current) => setIndex(current),
            }}
          />
        </div>
      )}

      {count > 1 && (
        <div className="mt-2 flex gap-2 overflow-x-auto">
          {photos.map((src, i) => (
            <button
              key={`${src}-${i}`}
              type="button"
              aria-label={`Show photo ${i + 1}`}
              aria-current={i === index ? "true" : undefined}
              onClick={() => setIndex(i)}
              className={`h-14 w-20 shrink-0 overflow-hidden rounded-md bg-light_gray transition-opacity ${
                i === index ? "outline outline-2 outline-primary" : "opacity-60 hover:opacity-100"
              }`}
            >
              <img
                alt=""
                src={src}
                loading="lazy"
                className="h-full w-full object-cover"
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ListingPhotoGallery;
